import { useEffect, useState } from 'react';
import { cachedImageUrl, loadImageUrl } from '../services/marketConditionsImageCache';

/**
 * Thumbnail for a saved market-conditions image. The image route is
 * permission-gated, so a bare <img src> would 401; fetch the blob through the
 * authenticated client and show an object URL instead (same pattern as
 * LegDocumentModal). Click opens the full image in a new tab, unless the host
 * handles the click itself -- set `interactive={false}` then, or the thumbnail
 * opens a tab on top of whatever the host does.
 */
export default function MarketConditionsImageThumb({ id, name, onRemove, className = '', interactive = true, heightClass = 'h-24' }: {
  id: number;
  name: string;
  onRemove?: () => void;
  className?: string;
  interactive?: boolean;
  /** Shrink the tile where several sit side by side in a narrow column. */
  heightClass?: string;
}) {
  // Seed from the cache so a tile already fetched paints on the first render
  // instead of flashing its placeholder.
  const [url, setUrl] = useState<string | null>(() => cachedImageUrl(id) ?? null);
  useEffect(() => {
    const ready = cachedImageUrl(id);
    if (ready) { setUrl(ready); return; }
    let cancelled = false;
    setUrl(null);
    loadImageUrl(id)
      .then(loaded => { if (!cancelled) setUrl(loaded); })
      .catch(() => { /* image removed since the list loaded; next poll drops it */ });
    return () => { cancelled = true; };
  }, [id]);

  return <div className={`relative ${className}`}>
    {url
      ? <img src={url} alt={name} title={interactive ? 'Open full size' : undefined}
          className={`${heightClass} w-full cursor-pointer rounded-md border border-gray-300 object-cover hover:opacity-90 dark:border-gray-600`}
          onClick={interactive ? () => window.open(url, '_blank', 'noopener') : undefined} />
      : <div className={`flex ${heightClass} w-full items-center justify-center overflow-hidden rounded-md border border-dashed border-gray-300 px-1 text-center text-[10px] leading-tight text-muted dark:border-gray-600`}>{name}</div>}
    {onRemove && <button type="button" aria-label={`Remove ${name}`} onClick={onRemove} className="absolute right-1 top-1 rounded-full bg-red-600 p-1 text-white hover:bg-red-700">
      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
    </button>}
  </div>;
}
